"use client";

import React, { useEffect, useRef } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { ArrowLeft, MoreHorizontal } from "lucide-react";
import { ButtonIcon, ButtonOutline } from "@/components/common/StandardButton";
import { NavigationBreadcrumb } from "@/components/common/NavigationBreadcrumb";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { orderActions } from "@/store/actions/order.actions";
import { PaymentType } from "@/types/models/order";
import CustomerInformation from "@/components/orders/CustomerInformation";
import OrderSetsList from "@/components/orders/ProductsList";
import FullInvoice from "@/components/orders/FullInvoice";
import ShipmentDetails from "@/components/orders/ShipmentDetails";
import OrderActions from "@/components/orders/OrderActions";
import RecentOrdersCard from "@/components/orders/RecentOrdersCard";
import OrderTrackingCard from "@/components/orders/OrderTrackingCard";
import SupportCard from "@/components/orders/SupportCard";
import { usePermissions } from "@/hooks/usePermissions";

export default function OrderDetailsPage() {
  const { orderId, storeType, storeId } = useParams<{
    orderId: string;
    storeType: string;
    storeId: string;
  }>();
  const { isWholesaler } = usePermissions();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { selectedOrder, detailsLoading } = useAppSelector(
    (state) => state.order,
  );

  // Track if we've already fetched this order to prevent duplicate requests
  const hasFetchedRef = useRef<string | null>(null);
  const shouldRedirectToOrderSets = Boolean(
    isWholesaler && storeType && storeId,
  );

  useEffect(() => {
    if (shouldRedirectToOrderSets) return;

    // Skip if already loading or if we already have this order
    if (detailsLoading) {
      console.log("[OrderDetailsPage] Already loading, skipping fetch");
      return;
    }

    // If we already have this order in state, skip fetch
    if (selectedOrder?.id === orderId) {
      console.log("[OrderDetailsPage] Order already in state, skipping fetch");
      return;
    }

    // If we've already initiated fetch for this orderId, skip
    if (orderId && hasFetchedRef.current === orderId) {
      console.log("[OrderDetailsPage] Already fetched this order, skipping");
      return;
    }

    if (orderId) {
      console.log("[OrderDetailsPage] Fetching order details for:", orderId);
      hasFetchedRef.current = orderId;
      dispatch(orderActions.fetchOrderDetails(orderId));
    }
  }, [
    orderId,
    selectedOrder?.id,
    detailsLoading,
    dispatch,
    shouldRedirectToOrderSets,
  ]);

  // Reset the ref when orderId changes (navigating to different order)
  useEffect(() => {
    return () => {
      if (orderId !== hasFetchedRef.current) {
        console.log("[OrderDetailsPage] Order changed, resetting fetch ref");
        hasFetchedRef.current = null;
      }
    };
  }, [orderId]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const breadcrumbItems = [
    {
      label: "Orders",
      href: `/${storeType}/${storeId}/orders`,
    },
    {
      label: `Order #${orderId}`,
    },
  ];

  const getPaymentMethodBadge = (paymentType?: PaymentType) => {
    if (!paymentType) return null;

    const config = {
      PREPAID: { label: "Prepaid", class: "bg-green-100 text-green-800" },
      POSTPAID: { label: "COD", class: "bg-orange-100 text-orange-800" },
      SEMIPREPAID: {
        label: "Partial Payment",
        class: "bg-blue-100 text-blue-800",
      },
    };

    const paymentConfig = config[paymentType];

    return (
      <span
        className={`px-3 py-1 ${paymentConfig.class} text-sm font-medium rounded-full`}
      >
        {paymentConfig.label}
      </span>
    );
  };

  if (shouldRedirectToOrderSets) {
    return <Navigate to={`/${storeType}/${storeId}/order-sets`} replace />;
  }

  return (
    <div className="view min-h-screen bg-background gap-2.5 flex flex-col">
      {/* Breadcrumb */}
      <NavigationBreadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="border-b border-border bg-card rounded-lg">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <ButtonOutline
              onClick={handleBackClick}
              className="flex items-center gap-2 px-3"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </ButtonOutline>

            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-lg font-semibold text-foreground">
                  Order #{orderId}
                </h1>
                <p className="text-sm text-muted-foreground">
                  Mon, May 16 2022, 2:11 AM
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <ButtonOutline size="sm" className="text-xs">
              Print
            </ButtonOutline>
            <ButtonOutline size="sm" className="text-xs">
              Return-to-Origin (RTO)
            </ButtonOutline>
            <ButtonIcon variant="outline">
              <MoreHorizontal className="h-4 w-4" />
            </ButtonIcon>
          </div>
        </div>
      </div>

      {/* Main Content - Three Columns */}
      <div className="flex gap-2.5 flex-1">
        {/* Column 1 - Customer Info, Recent Orders, Order Tracking */}
        <div className="column w-80 flex flex-col gap-2.5">
          <CustomerInformation orderId={orderId!} />
          <RecentOrdersCard selectedOrder={selectedOrder} storeId={storeId!} />
          <OrderTrackingCard orderId={orderId!} />
        </div>

        {/* Column 3 - Shipment Details, Support */}

        {/* Column 2 - Action Buttons, Order Sets List, Invoice */}
        <div className="column flex-1 flex flex-col gap-2.5">
          {/* Status and Action Buttons */}
          <div className="flex items-center justify-between p-4 bg-card rounded-lg border border-border">
            <div className="flex items-center gap-3">
              {detailsLoading ? (
                <div className="h-6 w-16 bg-muted rounded-full animate-pulse"></div>
              ) : (
                getPaymentMethodBadge(
                  selectedOrder?.used_payment_type ||
                    selectedOrder?.supported_payment_type?.[0],
                )
              )}
              <span className="text-sm text-muted-foreground">
                {selectedOrder?.current_order_stage
                  ?.replace(/_/g, " ")
                  .toLowerCase()
                  .replace(/\b\w/g, (l: string) => l.toUpperCase()) ||
                  "Processing"}
              </span>
            </div>
            <OrderActions orderId={orderId!} />
          </div>

          <OrderSetsList orderId={orderId!} />
          <div className="flex gap-2.5">
            {/* Show shipment details for single order set, or just support for multiple */}
            <div className="column w-80 flex flex-col gap-2.5">
              {selectedOrder?.order_sets?.length === 1 && (
                <ShipmentDetails orderId={orderId!} />
              )}
              <SupportCard orderId={orderId!} />
            </div>
            <FullInvoice orderId={orderId!} />
          </div>
        </div>
      </div>
    </div>
  );
}
